/**
 * DonutChart — 공용 SVG 도넛 (ECharts 폐기, 2026-09-04).
 *
 *   조각 hover 강조(살짝 확대 + 나머지 dim) · 상위 N 외곽 라벨(지시선) · 공용 ChartTooltip.
 *   중앙 텍스트는 caller 가 overlay 로 올린다 (.sd-center 패턴).
 *
 *   <DonutChart items={[{label, value}]} size={200} outerLabels={5} tooltip={(it, pct) => …} />
 */
import { useMemo, useRef, useState, type ReactNode } from "react";
import { ChartTooltip } from "@/components/ui/ChartTooltip";
import { useHoverDismiss } from "@/components/ui/useHoverDismiss";
import { paletteColor } from "./tokens";

export interface DonutItem {
  label: string;
  value: number;
  color?: string;
}

interface Props {
  items: DonutItem[];
  /** 도넛 지름(px). 외곽 라벨이 있으면 좌우로 라벨 폭이 더해진다 */
  size?: number;
  /** 두께 비율 (0~1, 반지름 대비). 기본 0.3 */
  thickness?: number;
  /** 외곽 라벨을 붙일 상위 조각 수. 0 = 없음 */
  outerLabels?: number;
  /** 툴팁 내용. 없으면 라벨·값·비중 기본 행 */
  tooltip?: (item: DonutItem, pct: number, index: number) => ReactNode;
  /** 조각 값 표기 (기본 툴팁·라벨용) */
  valueFormatter?: (v: number) => string;
  unit?: string;
  ariaLabel?: string;
  className?: string;
  /** 바깥(범례 등)에서 강조할 조각 index. 조각 hover 가 있으면 그쪽이 우선 */
  activeIndex?: number | null;
  /** 조각 hover 가 바뀔 때(벗어나면 null) — 범례 강조 연동용 */
  onActiveChange?: (index: number | null) => void;
  /** 툴팁 끄기 — 범례가 같은 정보를 보여줄 때 */
  hideTooltip?: boolean;
}

const LABEL_W = 84;
const GAP_DEG = 1.2;

function polar(cx: number, cy: number, r: number, deg: number) {
  const a = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

function arcPath(cx: number, cy: number, r0: number, r1: number, a0: number, a1: number): string {
  const large = a1 - a0 > 180 ? 1 : 0;
  const p0 = polar(cx, cy, r1, a0);
  const p1 = polar(cx, cy, r1, a1);
  const p2 = polar(cx, cy, r0, a1);
  const p3 = polar(cx, cy, r0, a0);
  return [
    `M ${p0.x.toFixed(2)} ${p0.y.toFixed(2)}`,
    `A ${r1} ${r1} 0 ${large} 1 ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`,
    `L ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`,
    `A ${r0} ${r0} 0 ${large} 0 ${p3.x.toFixed(2)} ${p3.y.toFixed(2)}`,
    "Z",
  ].join(" ");
}

export function DonutChart({
  items,
  size = 200,
  thickness = 0.3,
  outerLabels = 0,
  tooltip,
  valueFormatter = (v) => v.toLocaleString("ko-KR"),
  unit = "",
  ariaLabel,
  className,
  activeIndex = null,
  onActiveChange,
  hideTooltip = false,
}: Props) {
  const [hover, setHoverState] = useState<{ i: number; clientX: number; clientY: number } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const lastIndex = useRef<number | null>(null);
  // hover 갱신 + 조각이 바뀔 때만 바깥에 알린다(마우스 이동마다 부르지 않게)
  const setHover = (next: { i: number; clientX: number; clientY: number } | null) => {
    setHoverState(next);
    const i = next?.i ?? null;
    if (i !== lastIndex.current) {
      lastIndex.current = i;
      onActiveChange?.(i);
    }
  };
  useHoverDismiss(hover != null, () => setHover(null), svgRef);
  const total = useMemo(() => items.reduce((s, it) => s + Math.max(0, it.value), 0), [items]);

  const padX = outerLabels > 0 ? LABEL_W + 12 : 0;
  const W = size + padX * 2;
  const H = size;
  const cx = W / 2;
  const cy = H / 2;
  const r1 = size / 2 - 2;
  const r0 = r1 * (1 - thickness);

  // 각 조각의 시작/끝 각도 (12시 기준 시계방향)
  const slices = useMemo(() => {
    type Slice = { it: DonutItem; i: number; a0: number; a1: number; mid: number; pct: number; color: string };
    const out: Slice[] = [];
    items.forEach((it, i) => {
      const v = Math.max(0, it.value);
      const deg = total > 0 ? (v / total) * 360 : 0;
      const a0 = out.length ? out[out.length - 1].a1 : 0;
      const a1 = a0 + deg;
      out.push({ it, i, a0, a1, mid: (a0 + a1) / 2, pct: total > 0 ? (v / total) * 100 : 0, color: it.color ?? paletteColor(i) });
    });
    return out;
  }, [items, total]);

  if (items.length === 0 || total <= 0) {
    return (
      <div className={`chart-host donut-host${className ? ` ${className}` : ""}`} style={{ height: size }}>
        <div className="chart-empty">데이터 없음</div>
      </div>
    );
  }

  const active = hover?.i ?? activeIndex;

  return (
    <div className={`chart-host donut-host${className ? ` ${className}` : ""}`} style={{ height: size }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={ariaLabel}
        onMouseLeave={() => setHover(null)}
      >
        {slices.map((s) => {
          if (s.a1 - s.a0 <= 0) return null;
          // 조각 사이 틈 — 아주 작은 조각은 틈을 줄여 사라지지 않게
          const gap = Math.min(GAP_DEG, (s.a1 - s.a0) / 3);
          const d = arcPath(cx, cy, r0, r1, s.a0 + gap / 2, s.a1 - gap / 2);
          const isActive = active === s.i;
          const dim = active != null && !isActive;
          return (
            <path
              key={s.i}
              d={d}
              fill={s.color}
              className={`donut-slice${isActive ? " is-active" : ""}${dim ? " is-dim" : ""}`}
              style={{ transformOrigin: `${cx}px ${cy}px` }}
              onMouseMove={(e) => setHover({ i: s.i, clientX: e.clientX, clientY: e.clientY })}
            />
          );
        })}
        {outerLabels > 0 && slices.slice(0, outerLabels).map((s) => {
          if (s.a1 - s.a0 <= 0) return null;
          const right = s.mid <= 180;
          const p1 = polar(cx, cy, r1 + 4, s.mid);
          const p2 = polar(cx, cy, r1 + 14, s.mid);
          const x3 = right ? cx + r1 + 26 : cx - r1 - 26;
          const tx = right ? x3 + 4 : x3 - 4;
          const dim = active != null && active !== s.i;
          return (
            <g key={`lb-${s.i}`} className={`donut-label${dim ? " is-dim" : ""}`}>
              <polyline
                points={`${p1.x.toFixed(1)},${p1.y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)} ${x3},${p2.y.toFixed(1)}`}
                fill="none"
                stroke={s.color}
                strokeWidth="1.3"
              />
              <text x={tx} y={p2.y - 3} textAnchor={right ? "start" : "end"} fontSize="12" fontWeight="700" fill={s.color}>
                {s.it.label}
              </text>
              <text x={tx} y={p2.y + 10} textAnchor={right ? "start" : "end"} fontSize="11" fontWeight="600" fill="var(--c-text-muted)">
                {Math.round(s.pct)}%
              </text>
            </g>
          );
        })}
      </svg>
      {!hideTooltip && hover && slices[hover.i] && (
        <ChartTooltip x={hover.clientX} y={hover.clientY}>
          {tooltip ? (
            tooltip(slices[hover.i].it, slices[hover.i].pct, hover.i)
          ) : (
            <div className="dc-tip">
              <div className="dc-tip-head donut-tip-head">
                <span className="dc-tip-dot" style={{ background: slices[hover.i].color }} />
                {slices[hover.i].it.label}
              </div>
              <div className="dc-tip-rows">
                <div className="dc-tip-row is-kv">
                  <span className="dc-tip-label">비중</span>
                  <span className="dc-tip-val">{Math.round(slices[hover.i].pct)}%</span>
                </div>
                <div className="dc-tip-row is-kv">
                  <span className="dc-tip-label">값</span>
                  <span className="dc-tip-val">
                    {valueFormatter(slices[hover.i].it.value)}
                    {unit && <span className="dc-tip-unit">{unit}</span>}
                  </span>
                </div>
              </div>
            </div>
          )}
        </ChartTooltip>
      )}
    </div>
  );
}
